import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  #isAdding = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState
    );

    this.#updateButtonLabels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState
    );
  }

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const hotspotProduct = this.closest(
      'product-hotspot-component'
    );

    const productLink =
      productCard?.getProductCardLink?.() ||
      hotspotProduct?.getHotspotProductLink?.();

    if (!productLink?.href) return '';

    const url = new URL(
      productLink.href,
      window.location.origin
    );

    if (!url.searchParams.has('variant')) {
      const selectedVariantId = this.#getSelectedVariantId();

      if (selectedVariantId) {
        url.searchParams.set('variant', selectedVariantId);
      }
    }

    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = this.closest('product-card');

    return productCard?.getSelectedVariantId?.() || null;
  }

  #updateButtonLabels() {
    const labels = this.querySelectorAll(
      '.quick-add__button--choose .quick-add__button-text'
    );

    for (const label of labels) {
      label.textContent = 'Ajouter';
    }

    const chooseButtons = this.querySelectorAll(
      '.quick-add__button--choose'
    );

    for (const button of chooseButtons) {
      button.setAttribute('aria-label', 'Ajouter');
    }
  }

  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const productCard = this.closest('product-card');

    if (
      !productCard ||
      event.target.closest('product-card') !== productCard
    ) {
      return;
    }

    const productOptionsCount =
      this.dataset.productOptionsCount;

    this.setAttribute(
      'data-quick-add-button',
      productOptionsCount === '1' ? 'add' : 'choose'
    );

    this.#updateButtonLabels();
  };

  /**
   * Utilise la variante sélectionnée ou liée à la carte.
   * À défaut, utilise la première variante disponible.
   */
  async #getVariantId(productPageUrl) {
    const selectedVariantId = this.#getSelectedVariantId();

    if (selectedVariantId) {
      return String(selectedVariantId);
    }

    if (!productPageUrl) {
      throw new Error(
        'Impossible d’identifier ce produit. Rechargez la page.'
      );
    }

    const productUrl = new URL(productPageUrl);
    const linkedVariantId =
      productUrl.searchParams.get('variant');

    if (linkedVariantId) {
      return linkedVariantId;
    }

    productUrl.pathname =
      productUrl.pathname.replace(/\/$/, '') + '.js';

    productUrl.search = '';
    productUrl.hash = '';

    const response = await fetch(productUrl.toString(), {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(
        'Impossible de charger ce produit. Réessayez.'
      );
    }

    const product = await response.json();

    const availableVariant = product.variants?.find(
      (variant) => variant.available
    );

    if (!availableVariant) {
      throw new Error(
        'Ce produit est actuellement indisponible.'
      );
    }

    return String(availableVariant.id);
  }

  /**
   * Ajoute un exemplaire, puis ouvre le panier.
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isAdding) return;

    this.#isAdding = true;

    const button =
      event.target instanceof Element
        ? event.target.closest('button')
        : null;

    const wasDisabled = button?.disabled ?? false;
    const previousBusy = button?.getAttribute('aria-busy');

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    let added = false;

    try {
      const productPageUrl = this.productPageUrl;
      const variantId = await this.#getVariantId(
        productPageUrl
      );

      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      let result = null;

      try {
        result = await response.json();
      } catch {
        // Le statut HTTP détermine si l’ajout a réussi.
      }

      if (!response.ok) {
        const message =
          typeof result?.description === 'string'
            ? result.description
            : typeof result?.message === 'string'
              ? result.message
              : 'Impossible d’ajouter ce produit au panier. Réessayez.';

        throw new Error(message);
      }

      added = true;

      window.location.assign(`${root}cart`);
    } catch (error) {
      console.error(
        'SOIDOR : erreur d’ajout au panier.',
        error
      );

      window.alert(
        added
          ? 'Le produit a été ajouté. Ouvrez votre panier pour continuer.'
          : error instanceof Error
            ? error.message
            : 'Impossible d’ajouter ce produit au panier. Réessayez.'
      );
    } finally {
      // Évite un double ajout pendant l’ouverture du panier.
      if (!added) {
        this.#isAdding = false;

        if (button) {
          button.disabled = wasDisabled;

          if (previousBusy === null) {
            button.removeAttribute('aria-busy');
          } else if (previousBusy !== undefined) {
            button.setAttribute('aria-busy', previousBusy);
          }
        }
      }
    }
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}

/**
 * Composant conservé pour la compatibilité du thème.
 * Le bouton Ajouter n’ouvre plus cette fenêtre.
 */
class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const options = {
      signal: this.#abortController.signal,
    };

    this.addEventListener(
      ThemeEvents.cartUpdate,
      this.handleCartUpdate,
      options
    );

    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink,
      options
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose,
      options
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;

    this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchorElement =
      event.detail?.data?.html?.querySelector(
        '.view-product-title a'
      );

    if (!anchorElement) return;

    const viewMoreDetailsLink = this.querySelector(
      '.view-product-title a'
    );

    const mobileProductTitle = this.querySelector(
      '.product-header a'
    );

    if (viewMoreDetailsLink) {
      viewMoreDetailsLink.href = anchorElement.href;
    }

    if (mobileProductTitle) {
      mobileProductTitle.href = anchorElement.href;
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 && iosVersion.minor >= 4)
    ) {
      return;
    }

    requestAnimationFrame(() => {
      const grid = document.querySelector(
        '#ResultsList [product-grid-view]'
      );

      if (!(grid instanceof HTMLElement)) return;

      const currentWidth = grid.getBoundingClientRect().width;

      grid.style.width = `${currentWidth - 1}px`;

      requestAnimationFrame(() => {
        grid.style.width = '';
      });
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}